import type { Input } from './Input';

/**
 * Touch input for iOS/mobile (spec 0029): a floating-origin move stick
 * (bottom-left), a look-drag surface (everywhere else), and buttons for
 * jump / restart / menu. Everything drives the existing Input synthetic
 * layer, so the game loop's reads (pressed/axis/consumeMouseDelta) are
 * untouched. Pointer Events only (iOS 13+), tracked per pointerId;
 * `pointercancel` (iOS edge swipes, system gestures) always releases.
 */

/** Screen-pixels of thumb drag → synthetic mouse pixels (thumb travel is short). */
export const TOUCH_LOOK_SCALE = 2.5;
export const STICK_RADIUS = 56;
export const STICK_DEADZONE = 10;

/** Pure decision core (unit-testable): override always wins. */
export function isTouchDevice(
  coarsePointer: boolean,
  maxTouchPoints: number,
  override: boolean,
): boolean {
  return override || (coarsePointer && maxTouchPoints > 0);
}

/**
 * True when the primary pointer is a finger. `?touch` in the URL forces touch
 * mode (Playwright emulation + desktop debugging).
 */
export function detectTouchMode(): boolean {
  return isTouchDevice(
    window.matchMedia('(pointer: coarse)').matches,
    navigator.maxTouchPoints,
    new URLSearchParams(window.location.search).has('touch'),
  );
}

/**
 * Maps a stick drag vector (screen px, +y down) to movement axes in [-1,1].
 * Inside the deadzone → zero; magnitude rescales from the deadzone edge to the
 * radius. Direction is what matters downstream (wishDirFromInput normalizes).
 */
export function stickToAxes(
  dx: number,
  dy: number,
  radius: number = STICK_RADIUS,
  deadzone: number = STICK_DEADZONE,
): { forward: number; right: number } {
  const mag = Math.hypot(dx, dy);
  if (mag < deadzone) {
    return { forward: 0, right: 0 };
  }
  const scaled = Math.min(1, (mag - deadzone) / Math.max(1, radius - deadzone));
  return {
    forward: (-dy / mag) * scaled, // screen-up = forward
    right: (dx / mag) * scaled,
  };
}

interface StickState {
  pointerId: number;
  originX: number;
  originY: number;
}

interface LookState {
  pointerId: number;
  lastX: number;
  lastY: number;
}

export class TouchControls {
  private readonly root: HTMLElement;
  private readonly stickZone: HTMLElement;
  private readonly stickRing: HTMLElement;
  private readonly stickNub: HTMLElement;
  private stick: StickState | null = null;
  private look: LookState | null = null;

  constructor(
    private readonly input: Input,
    hooks: { onMenu: () => void },
  ) {
    this.root = document.createElement('div');
    this.root.id = 'touch-ui';
    this.root.style.display = 'none';

    // Look surface: the whole screen below the other controls.
    const lookLayer = document.createElement('div');
    lookLayer.className = 'touch-look';
    this.attachLook(lookLayer);

    // Move stick zone (bottom-left) with a floating ring + nub.
    this.stickZone = document.createElement('div');
    this.stickZone.className = 'touch-stick-zone';
    this.stickRing = document.createElement('div');
    this.stickRing.className = 'touch-stick-ring';
    this.stickNub = document.createElement('div');
    this.stickNub.className = 'touch-stick-nub';
    this.stickRing.style.display = 'none';
    this.stickRing.appendChild(this.stickNub);
    this.stickZone.appendChild(this.stickRing);
    this.attachStick(this.stickZone);

    const jump = this.button('touch-jump', 'JUMP', (down) =>
      this.input.setSynthetic('Space', down),
    );
    const restart = this.button('touch-restart', '↻', (down) =>
      this.input.setSynthetic('KeyR', down),
    );
    const menu = this.button('touch-menu', '☰', (down) => {
      if (down) hooks.onMenu();
    });

    this.root.append(lookLayer, this.stickZone, jump, restart, menu);
    document.body.appendChild(this.root);
    // iOS: suppress long-press callout/selection anywhere on the control layer.
    this.root.addEventListener('contextmenu', (e) => e.preventDefault());
    // Kill the browser's synthesized "ghost click" after taps: when the menu
    // button hides this layer, that click would land on the overlay underneath
    // and instantly restart the session. Pointer events already fired.
    this.root.addEventListener('touchend', (e) => e.preventDefault(), { passive: false });
  }

  show(): void {
    this.root.style.display = 'block';
  }

  hide(): void {
    this.root.style.display = 'none';
    this.releaseStick();
    this.look = null;
  }

  private button(id: string, label: string, set: (down: boolean) => void): HTMLElement {
    const el = document.createElement('button');
    el.id = id;
    el.className = 'touch-button';
    el.type = 'button';
    el.textContent = label;
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      el.setPointerCapture(e.pointerId);
      set(true);
    });
    const release = () => set(false);
    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', release);
    return el;
  }

  private attachStick(zone: HTMLElement): void {
    zone.addEventListener('pointerdown', (e) => {
      if (this.stick) return; // one stick pointer at a time
      e.preventDefault();
      zone.setPointerCapture(e.pointerId);
      this.stick = { pointerId: e.pointerId, originX: e.clientX, originY: e.clientY };
      this.stickRing.style.display = 'block';
      this.stickRing.style.left = `${e.clientX}px`;
      this.stickRing.style.top = `${e.clientY}px`;
      this.moveNub(0, 0);
    });
    zone.addEventListener('pointermove', (e) => {
      if (this.stick?.pointerId !== e.pointerId) return;
      const dx = e.clientX - this.stick.originX;
      const dy = e.clientY - this.stick.originY;
      const { forward, right } = stickToAxes(dx, dy);
      this.input.setSyntheticAxis('KeyW', 'KeyS', forward);
      this.input.setSyntheticAxis('KeyD', 'KeyA', right);
      this.moveNub(dx, dy);
    });
    const release = (e: PointerEvent) => {
      if (this.stick?.pointerId !== e.pointerId) return;
      this.releaseStick();
    };
    zone.addEventListener('pointerup', release);
    zone.addEventListener('pointercancel', release);
  }

  private releaseStick(): void {
    this.stick = null;
    this.input.setSyntheticAxis('KeyW', 'KeyS', 0);
    this.input.setSyntheticAxis('KeyD', 'KeyA', 0);
    this.stickRing.style.display = 'none';
  }

  private moveNub(dx: number, dy: number): void {
    const mag = Math.hypot(dx, dy);
    const clamp = mag > STICK_RADIUS ? STICK_RADIUS / mag : 1;
    this.stickNub.style.transform = `translate(calc(-50% + ${dx * clamp}px), calc(-50% + ${dy * clamp}px))`;
  }

  private attachLook(layer: HTMLElement): void {
    layer.addEventListener('pointerdown', (e) => {
      if (this.look) return; // first look pointer wins
      e.preventDefault();
      layer.setPointerCapture(e.pointerId);
      this.look = { pointerId: e.pointerId, lastX: e.clientX, lastY: e.clientY };
    });
    layer.addEventListener('pointermove', (e) => {
      if (this.look?.pointerId !== e.pointerId) return;
      this.input.injectMouseDelta(
        (e.clientX - this.look.lastX) * TOUCH_LOOK_SCALE,
        (e.clientY - this.look.lastY) * TOUCH_LOOK_SCALE,
      );
      this.look.lastX = e.clientX;
      this.look.lastY = e.clientY;
    });
    const release = (e: PointerEvent) => {
      if (this.look?.pointerId === e.pointerId) {
        this.look = null;
      }
    };
    layer.addEventListener('pointerup', release);
    layer.addEventListener('pointercancel', release);
  }
}
